import React from "react";
import { Funnel } from "lucide-react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { OrganizationPartnershipLevelEnum } from "@/__generated__/types";
import { useDialogContext } from "@/components/DialogProvider";

interface FilterPillProps {
  filterCount: number;
  onClick?: () => void;
}

export const FilterPill = ({
  filterCount,
  onClick,
  ...props
}: FilterPillProps & React.ComponentProps<"button">) => {
  const { t } = useTranslation();
  const { showPermissionDialog } = useDialogContext();
  const { hasMinOrgPartnershipLevel } = useOrgPermissions();

  const canUseFilters =
    hasMinOrgPartnershipLevel(OrganizationPartnershipLevelEnum.Pro) ?? false;
  const isActive = filterCount > 0;

  const handleClick = () => {
    if (canUseFilters) {
      onClick?.();
    } else {
      showPermissionDialog();
    }
  };

  return (
    <button
      className={clsx(
        "inline-flex h-10 items-center gap-3 rounded-full border bg-background px-6 py-2 hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50 [&_svg:not([class*='size-'])]:size-4",
        {
          "border-2 border-foreground": isActive,
        },
      )}
      onClick={handleClick}
      {...props}
    >
      {t("global.filters")}
      <Funnel aria-hidden={true} />
      {isActive && (
        <div className="h-5 min-w-5 rounded-full bg-foreground px-1.5 text-[13px] font-bold text-background">
          {filterCount}
        </div>
      )}
    </button>
  );
};
